import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { Card } from "primereact/card";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import classNames from "classnames";

const MAX_ACTIVITY_POINTS = 1000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActivityContentValues {
  totalPoints?: number;
  [key: string]: unknown;
}

interface ValidationError {
  hasError: boolean;
  message: string;
}

interface ActivityPathContentStepProps {
  values: ActivityContentValues;
  handleChange: (newValues: ActivityContentValues) => void;
  showValidationMessages: boolean;
  validationErrors: ValidationError;
  minPoints?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ActivityPathContentStep = ({
  values,
  handleChange,
  showValidationMessages,
  validationErrors,
  minPoints = 0,
}: ActivityPathContentStepProps) => {
  const min = minPoints;
  const max = minPoints + MAX_ACTIVITY_POINTS;
  const totalPoints = Math.max(values.totalPoints ?? min, min);

  const handlePointsChange = (newValue: number | null) => {
    const clamped = Math.min(max, Math.max(min, newValue ?? min));
    handleChange({ ...values, totalPoints: clamped });
  };

  return (
    <>
      <p className="mb-6 text-secondary text-left">
        This is an activity-based path. Instead of selecting lessons, set the number of points
        learners must earn to complete this level.
      </p>

      <Card className={classNames(themeClasses.bgElevation2, "min-h-52 w-full 2xl:max-w-2/3")}>
        <h3 className="body-lg font-semibold mb-6">
          How many points do you want to require for this level?
        </h3>

        <div className="flex items-center flex-stretch gap-6 mb-4">
          <div className="w-full mr-4">
            <Slider
              value={totalPoints}
              onChange={e => handlePointsChange(e.value as number)}
              min={min}
              max={max}
              aria-label="Required points slider"
            />
          </div>
          <div className="w-20 text-right my-6 2xl:my-0">
            <InputNumber
              id="points-input"
              value={totalPoints}
              onValueChange={e => handlePointsChange(e.value ?? null)}
              min={min}
              max={max}
              showButtons
              buttonLayout="vertical"
              style={{ width: "5rem" }}
              decrementButtonClassName="p-button-secondary"
              incrementButtonClassName="p-button-secondary"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
              aria-label="Required points"
            />
          </div>
        </div>

        <p className="body-sm text-secondary">
          {totalPoints} points &mdash; range: {min.toLocaleString()}–{max.toLocaleString()}
        </p>
      </Card>

      {showValidationMessages && validationErrors.hasError && (
        <div className="mt-4">
          <ValidationMessage severity="error" text={validationErrors.message} />
        </div>
      )}
    </>
  );
};

export default ActivityPathContentStep;
